import axios from 'axios';
import { API_BASE } from '../../config';
import {
  normalizeEmail, extractClientsPayload, formatLocalDate, historyOfferRows, dopasujDoCeny, przypiszSprzetWierszom,
} from './utils';
import { fetchPriceQuote } from '../../utils/priceQuote';

/**
 * Zapisane wiersze PDF jednej oferty z Historii (GET /api/sent-offers/:id/equipment-rows).
 * Lista ofert ich nie niesie (przegląd bezpieczeństwa 12.10.2026: jechały w każdej odpowiedzi
 * listy, przy każdym otwarciu Historii). `null` = oferta zapisana przed migracją 053.
 */
export const fetchSavedOfferRows = async (offerId) => {
  const res = await axios.get(`${API_BASE}/api/sent-offers/${encodeURIComponent(offerId)}/equipment-rows`, { withCredentials: true });
  const rows = res && res.data ? res.data.equipment_rows : null;
  return Array.isArray(rows) ? rows : null;
};

/**
 * Wiersze PDF oferty z Historii (test odbiorczy 11.10.2026): dawniej z cen bazowych sztuk, więc nie
 * sumowały się do „CENA”. Teraz te zapisane przy ofercie (ten sam dokument, który dostał klient),
 * a dla ofert sprzed ich zapisu z wyceny jak w formularzu.
 *
 * Z tych wierszy korzysta okno oferty, „Generuj ofertę” i „Wyślij mailem” (12.10.2026): okno
 * pokazywało dla ofert bez zapisanych wierszy stawki z karty sprzętu i każdą sztukę osobno, bez
 * ramki zestawu, a PDF z tego samego okna miał stawki z cennika i wiersz zestawu. Dlatego:
 *  - wiersze ze starej karty przeglądarki (bez numeru sztuki) dostają numer po opisie, a gdy
 *    się nie da (sprzęt zmieniono od wysyłki), wiersze powstają z wyceny, jak bez zapisu;
 *  - suma wierszy jest dopasowana do CENA tak samo jak na serwerze (utils.js#dopasujDoCeny),
 *    więc przy cenniku zmienionym od wysyłki okno pokazuje te same stawki co dokument.
 */
export async function historyRows(offer) {
  const saved = Array.isArray(offer?.equipment_rows)
    ? offer.equipment_rows
    : (offer?.id ? await fetchSavedOfferRows(offer.id) : null);
  if (saved && saved.length) {
    const bezNumeru = saved.some((w) => w && w.typ !== 'zestaw' && w.equipmentId == null);
    const wiersze = bezNumeru ? przypiszSprzetWierszom(saved, offer.equipment) : saved;
    if (wiersze) return dopasujDoCeny(wiersze, offer.subtotal);
  }
  const ids = Array.isArray(offer?.equipment_ids) && offer.equipment_ids.length
    ? offer.equipment_ids.filter(Boolean)
    : (offer?.equipment || []).map((eq) => eq?.id).filter(Boolean);
  const quote = await fetchPriceQuote(
    ids,
    formatLocalDate(new Date(offer.date_from)),
    formatLocalDate(new Date(offer.date_to))
  );
  return dopasujDoCeny(historyOfferRows({ ...offer, equipment_rows: null }, quote), offer.subtotal);
}

const CLIENTS_ENDPOINT = `${API_BASE}/api/clients`;

export const fetchClientByEmail = async (email) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;

  const endpoints = [
    `${CLIENTS_ENDPOINT}/search?query=${encodeURIComponent(normalized)}`,
    `${CLIENTS_ENDPOINT}?email=${encodeURIComponent(normalized)}`,
    CLIENTS_ENDPOINT
  ];

  for (const url of endpoints) {
    try {
      const res = await axios.get(url, { withCredentials: true });
      const list = extractClientsPayload(res.data);
      if (!list.length) continue;
      const match = list.find(client => normalizeEmail(client.email) === normalized);
      if (match) return match;
    } catch {
      /* ignore and try next endpoint */
    }
  }

  return null;
};
